"use client";

import { FileText, Pause, Play, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import type { LightboxItem } from "../message/media-lightbox";
import type { AttachmentChip } from "../types";

interface AttachmentPreviewProps {
  attachments: AttachmentChip[];
  /** Drop a chip from the staged list (cancels mid-upload too — the in-flight
   *  upload's resolved handler is a no-op once the chip is gone). */
  onRemove: (tempId: string) => void;
  /** Open the existing `MediaLightbox` on the tapped image/video/PDF. The
   *  caller owns the lightbox state — we hand it the previewable subset so
   *  left/right nav walks across all previewable attachments. */
  onOpenLightbox: (items: LightboxItem[], index: number) => void;
}

const isImage = (m: string) => m.startsWith("image/");
const isVideo = (m: string) => m.startsWith("video/");
const isAudio = (m: string) => m.startsWith("audio/");
const isPdf = (m: string) => m === "application/pdf";

/**
 * Staged attachment row above the composer. EVERY chip is a 64×64 square so
 * the row stays neat regardless of filename length — the filename is the
 * tile's hover tooltip (`title`). Tile bodies vary by mimetype:
 *   - image  → thumbnail, tap opens the `MediaLightbox`
 *   - video  → first-frame + play badge, tap opens the lightbox
 *   - audio  → tap to play/pause INLINE (one audio at a time)
 *   - PDF    → tap opens the lightbox (iframe-rendered)
 *   - other  → document icon (no tap action)
 *
 * A spinner overlay marks any chip whose upload is still in flight; the
 * remove (×) is always available so the visitor can cancel mid-upload.
 *
 * Extracted as its own component (same shape as `EmojiPicker`) so the
 * preview + playback logic isn't tangled with the composer's state machine.
 */
export function AttachmentPreview({
  attachments,
  onRemove,
  onOpenLightbox,
}: AttachmentPreviewProps) {
  const t = useTranslations("messages");

  // Inline audio playback. One audio element at a time — tapping a second
  // chip stops the first (one playback channel, no overlapping streams).
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingTempId, setPlayingTempId] = useState<string | null>(null);

  // Stop playback if the currently-playing chip is removed (or sent) so the
  // audio doesn't keep playing in the background after its chip is gone.
  useEffect(() => {
    if (!playingTempId) return;
    if (attachments.some((a) => a.tempId === playingTempId)) return;
    audioRef.current?.pause();
    audioRef.current = null;
    setPlayingTempId(null);
  }, [attachments, playingTempId]);
  // Tear down on unmount so a stray audio doesn't outlive the tab.
  useEffect(
    () => () => {
      audioRef.current?.pause();
      audioRef.current = null;
    },
    [],
  );

  const toggleAudio = (chip: AttachmentChip) => {
    if (!chip.url) return;
    // Same chip → pause.
    if (playingTempId === chip.tempId) {
      audioRef.current?.pause();
      setPlayingTempId(null);
      return;
    }
    // Different (or no) chip → stop any current playback, start the new one.
    audioRef.current?.pause();
    const audio = new Audio(chip.url);
    audio.onended = () => {
      setPlayingTempId((cur) => (cur === chip.tempId ? null : cur));
    };
    audio.play().catch(() => setPlayingTempId(null));
    audioRef.current = audio;
    setPlayingTempId(chip.tempId);
  };

  // Lightbox-previewable subset (image / video / PDF). Each tap opens the
  // lightbox at the tapped item's index within THIS list so left/right
  // navigates only across previewable attachments (skipping audio/other).
  const previewable = attachments.filter(
    (a) => !!a.url && (isImage(a.mimetype) || isVideo(a.mimetype) || isPdf(a.mimetype)),
  );
  const previewableItems: LightboxItem[] = previewable.map((a) => ({
    url: a.url as string,
    mimetype: a.mimetype,
    alt: a.filename,
  }));
  const openFor = (chip: AttachmentChip) => {
    const idx = previewable.findIndex((a) => a.tempId === chip.tempId);
    if (idx < 0) return;
    onOpenLightbox(previewableItems, idx);
  };

  return (
    <div className="flex flex-wrap gap-2">
      {attachments.map((a) => {
        // Spinner ONLY while an online upload is actually in flight. A pending
        // offline chip (bytes held, no `mediaId` yet) is NOT uploading — it
        // shows its preview ready to send (the queue uploads it on reconnect).
        const uploading = a.uploading === true;
        const removeLabel = t("removeAttachment");

        // IMAGE — thumbnail, opens lightbox.
        if (isImage(a.mimetype) && a.url) {
          return (
            <Tile
              key={a.tempId}
              uploading={uploading}
              onRemove={() => onRemove(a.tempId)}
              removeLabel={removeLabel}
              title={a.filename}
            >
              <button
                type="button"
                onClick={() => openFor(a)}
                className="block h-16 w-16 overflow-hidden rounded-wx-sm bg-wx-bg-elevated"
                aria-label={t("openPreview")}
              >
                {/* biome-ignore lint/performance/noImgElement: visitor-uploaded image of arbitrary host/dimensions; next/image needs known sizes + an allowlist */}
                <img src={a.url} alt={a.filename} className="h-full w-full object-cover" />
              </button>
            </Tile>
          );
        }

        // VIDEO — first-frame + play badge, opens lightbox.
        if (isVideo(a.mimetype) && a.url) {
          return (
            <Tile
              key={a.tempId}
              uploading={uploading}
              onRemove={() => onRemove(a.tempId)}
              removeLabel={removeLabel}
              title={a.filename}
            >
              <button
                type="button"
                onClick={() => openFor(a)}
                className="relative block h-16 w-16 overflow-hidden rounded-wx-sm bg-wx-bg-elevated"
                aria-label={t("openPreview")}
              >
                <video
                  src={a.url}
                  className="h-full w-full object-cover"
                  muted
                  playsInline
                  preload="metadata"
                />
                <span className="absolute inset-0 flex items-center justify-center bg-black/30 text-white">
                  <Play size={16} fill="currentColor" />
                </span>
              </button>
            </Tile>
          );
        }

        // AUDIO — square chip; tap toggles inline playback. The chip flips
        // to the brand surface while playing so it's obvious which file is on.
        if (isAudio(a.mimetype) && a.url) {
          const playing = playingTempId === a.tempId;
          const Glyph = playing ? Pause : Play;
          return (
            <Tile
              key={a.tempId}
              uploading={uploading}
              onRemove={() => onRemove(a.tempId)}
              removeLabel={removeLabel}
              title={a.filename}
            >
              <button
                type="button"
                onClick={() => toggleAudio(a)}
                aria-label={playing ? t("pause") : t("play")}
                className={cn(
                  "flex h-16 w-16 items-center justify-center rounded-wx-sm transition-colors",
                  playing
                    ? "bg-wx-primary text-wx-primary-fg"
                    : "bg-wx-bg-elevated text-wx-fg-muted hover:text-wx-fg",
                )}
              >
                <Glyph size={22} fill="currentColor" strokeWidth={0} />
              </button>
            </Tile>
          );
        }

        // PDF — square chip, opens lightbox iframe. Falls back to a static
        // icon if the upload is still in flight (no url yet).
        if (isPdf(a.mimetype) && a.url) {
          return (
            <Tile
              key={a.tempId}
              uploading={uploading}
              onRemove={() => onRemove(a.tempId)}
              removeLabel={removeLabel}
              title={a.filename}
            >
              <button
                type="button"
                onClick={() => openFor(a)}
                aria-label={t("openPreview")}
                className={cn(
                  "flex h-16 w-16 items-center justify-center rounded-wx-sm",
                  "bg-wx-bg-elevated text-wx-fg-muted transition-colors hover:text-wx-fg",
                )}
              >
                <FileText size={22} aria-hidden="true" />
              </button>
            </Tile>
          );
        }

        // OTHER (any unknown doc, or PDF still uploading) — static square
        // tile with the document icon. Filename surfaces on hover.
        return (
          <Tile
            key={a.tempId}
            uploading={uploading}
            onRemove={() => onRemove(a.tempId)}
            removeLabel={removeLabel}
            title={a.filename}
          >
            <div className="flex h-16 w-16 items-center justify-center rounded-wx-sm bg-wx-bg-elevated text-wx-fg-muted">
              <FileText size={22} aria-hidden="true" />
            </div>
          </Tile>
        );
      })}
    </div>
  );
}

/** Square 64×64 tile wrapper — owns the remove (×) corner button, the
 *  upload-in-flight spinner overlay, and the filename hover tooltip. Every
 *  preview kind composes the same chrome around its body. */
function Tile({
  children,
  uploading,
  onRemove,
  removeLabel,
  title,
}: {
  children: React.ReactNode;
  uploading: boolean;
  onRemove: () => void;
  removeLabel: string;
  title?: string;
}) {
  return (
    <div className="relative" title={title}>
      {children}
      {uploading && (
        <span
          aria-hidden="true"
          className={cn(
            "pointer-events-none absolute inset-0 flex items-center justify-center",
            "rounded-wx-sm bg-black/30",
          )}
        >
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
        </span>
      )}
      <button
        type="button"
        onClick={onRemove}
        aria-label={removeLabel}
        className={cn(
          "absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center",
          "rounded-full bg-wx-bg text-wx-fg-muted shadow ring-1 ring-wx-border",
          "transition-colors hover:text-wx-fg",
        )}
      >
        <X size={11} />
      </button>
    </div>
  );
}
